import React, { useState } from 'react';
import { View, ViewStyle, StyleProp, TextStyle, Switch } from 'react-native';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { CustomTooltip } from './CustomTooltip';
import { Hoverable } from 'react-native-web-hover';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type FormSwitchProps = {
    label?: string;
    errorMessage?: string;
    disabled?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    inputStyle?: StyleProp<TextStyle>;
    labelFontSize?: number;
    hintText?: string | React.ReactNode | React.ReactNode;
    hintTextConfig?: {
        tooltipStyle?: React.CSSProperties;
        tooltipDirection?: 'right' | 'left' | 'top';
        maxWidth?: number;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
};

export default function FormSwitch({
    label,
    labelFontSize,
    disabled,
    viewStyle,
    labelStyle,
    hintText,
    hintTextConfig,
    form,
    fieldName,
    customValidations,
}: FormSwitchProps) {
    const { styles } = useStyles(stylesheet);
    const [hintHovered, setHintHovered] = useState(false);

    disabled = !!disabled;

    const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

    if (customValidations) {
        rules.validate = { ...customValidations };
    }

    return <Controller
        control={form.control}
        name={fieldName}
        rules={rules}
        render={({ field }) => (
            <View style={[hintHovered && { zIndex: 9999 }, viewStyle]}>
                <View style={[{ flexDirection: 'row', columnGap: 5 }, hintText ? { zIndex: 9999 } : {}]}>
                    {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16)}>{label}</MyAppText>}
                    {hintText
                        ? <Hoverable>
                            {({ hovered }) => {
                                setHintHovered(hovered);
                                return <CustomTooltip
                                    content={hintText}
                                    tooltipStyle={hintTextConfig?.tooltipStyle}
                                    tooltipDirection={hintTextConfig?.tooltipDirection}
                                    maxWidth={hintTextConfig?.maxWidth}
                                />;
                            }}
                        </Hoverable>
                        : null
                    }
                </View>
                <Switch
                    trackColor={{ false: '#767577', true: '#81b0ff' }}
                    thumbColor={field.value ? '#f5dd4b' : '#f4f3f4'}
                    disabled={disabled}
                    onValueChange={(value) => {
                        field.onChange(value);
                    }}
                    value={field.value}
                />
            </View>
        )}
    />;

}

const stylesheet = createStyleSheet(theme => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
}));
